/**
 * R-78 — inline provider-precedence resolution at generation time.
 *
 * When post-gen audit detects tier-1 SED facts disagreeing with report
 * prose, the operator picks per-fact:
 *   - tier1: autocorrect narrative to match SED provider value
 *   - report: patch SED at the violation provenance path, then re-gate
 *
 * No full LLM regen — the draft report is finalized and persisted.
 */
import type { CanonicalFactKey } from '../../types/auditCanonicalFacts';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ReportTypeId } from '../../config/reportTypes';
import {
  applyPrecedenceFixesToReport,
  type PrecedenceViolation,
} from './providerPrecedenceVerifier';
import { applyPostGenAuditGates } from './composePostGenAudit';
import { applyMetaInstructionStripToReport } from '../metaInstructionStrip';
import { runSectionDepthGate } from '../sectionDepthGate';

export const PRECEDENCE_OVERRIDE_RATIONALE_MIN_CHARS = 40;

export type PrecedenceResolutionChoice = 'tier1' | 'report';

export const PRECEDENCE_FACT_LABELS: Partial<Record<CanonicalFactKey, string>> = {
  acreage: 'Site acreage',
  capacityMW: 'Capacity (MW)',
  floodZone: 'Flood zone',
  interconnectionDistanceKm: 'Interconnection distance',
  nearestSubstation: 'Nearest substation',
  voltageKV: 'Substation voltage',
  nuclearProximityKm: 'Nuclear proximity',
  gridOperator: 'Grid operator',
};

function setAtPath(root: Record<string, unknown>, parts: string[], value: unknown): void {
  let cur = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]!;
    const next = cur[key];
    if (next == null || typeof next !== 'object' || Array.isArray(next)) {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

/** Patch a tier-1 SED field using the violation's provenance path. */
export function patchSedForPrecedenceViolation(
  sed: SiteEvidenceDocument,
  violation: PrecedenceViolation,
): SiteEvidenceDocument {
  const provenancePath = violation.provenance ?? inferProvenanceFromFact(violation.fact);
  if (!provenancePath?.startsWith('sed.')) {
    throw new Error(`Cannot patch SED for ${violation.fact}: missing provenance`);
  }

  const next = JSON.parse(JSON.stringify(sed)) as SiteEvidenceDocument;
  const relPath = provenancePath.slice('sed.'.length);
  setAtPath(next as unknown as Record<string, unknown>, relPath.split('.'), violation.reportedValue);
  return next;
}

function inferProvenanceFromFact(fact: CanonicalFactKey): string | undefined {
  switch (fact) {
    case 'acreage': return 'sed.siteProfile.data.acres';
    case 'floodZone': return 'sed.flood.data.floodZone';
    case 'interconnectionDistanceKm': return 'sed.grid.data.substationDistKm';
    case 'nearestSubstation': return 'sed.grid.data.substationName';
    case 'voltageKV': return 'sed.grid.data.voltage';
    case 'gridOperator': return 'sed.grid.data.isoRegion';
    case 'nuclearProximityKm': return 'sed.nuclearInfra.data.nearestReactorDistKm';
    case 'capacityMW': return 'sed.fullProjectIntent.powerRequirementMW';
    default: return undefined;
  }
}

/** Apply operator choices to draft report + optional SED patch. */
export function applyPrecedenceResolutionChoices(
  draftReport: SiteRagReport,
  sed: SiteEvidenceDocument,
  violations: ReadonlyArray<PrecedenceViolation>,
  choices: ReadonlyMap<CanonicalFactKey, PrecedenceResolutionChoice>,
): { report: SiteRagReport; sed: SiteEvidenceDocument; reportValueFacts: CanonicalFactKey[] } {
  let nextReport = draftReport;
  let nextSed = sed;
  const reportValueFacts: CanonicalFactKey[] = [];

  const tier1Violations: PrecedenceViolation[] = [];
  for (const v of violations) {
    const choice = choices.get(v.fact) ?? 'tier1';
    if (choice === 'tier1') {
      tier1Violations.push(v);
    } else {
      reportValueFacts.push(v.fact);
      nextSed = patchSedForPrecedenceViolation(nextSed, v);
    }
  }

  if (tier1Violations.length > 0) {
    nextReport = applyPrecedenceFixesToReport(nextReport, tier1Violations, 'autocorrect');
  }

  return { report: nextReport, sed: nextSed, reportValueFacts };
}

export interface FinalizePrecedenceReportEnv {
  saveReport: (report: SiteRagReport, sed: SiteEvidenceDocument, reportTypeId: ReportTypeId) => Promise<boolean>;
  onReportGenerated: (report: SiteRagReport) => void;
  updateSiteReportIds: (siteId: string, reportId: string) => Promise<void>;
  applyReportToSed?: (report: SiteRagReport, siteId: string, sedId: string) => Promise<void>;
}

export interface FinalizePrecedenceReportContext {
  siteId: string;
  reportTypeId: ReportTypeId;
  documents?: SiteDocument[];
  /** Live intent for multi-level fact acceptance (voltage altValues);
   *  absent falls back to the SED's at-compile fullProjectIntent. */
  projectIntent?: import('../../types/siteRag').ProjectIntent | null;
}

/** Re-run post-gen gates and persist after precedence resolution. */
export async function finalizeReportAfterPrecedenceResolution(
  draftReport: SiteRagReport,
  sed: SiteEvidenceDocument,
  ctx: FinalizePrecedenceReportContext,
  env: FinalizePrecedenceReportEnv,
): Promise<SiteRagReport> {
  let report = await applyPostGenAuditGates(draftReport, {
    correctReliabilityMarkers: true,
    sed,
    documents: ctx.documents ?? [],
    siteId: ctx.siteId,
    reportId: draftReport.id,
    reportType: ctx.reportTypeId,
    projectIntent: ctx.projectIntent,
  });

  report = applyMetaInstructionStripToReport(report);
  const { applySectionDedupFindingsToReport } = await import('../sectionContentDedup');
  report = applySectionDedupFindingsToReport(report);
  const { applyCaveatHonestyCheckToReport } = await import('../caveatHonestyGate');
  report = applyCaveatHonestyCheckToReport(report);
  runSectionDepthGate(report);

  const saved = await env.saveReport(report, sed, ctx.reportTypeId);
  if (!saved) {
    console.warn('[finalizeReportAfterPrecedenceResolution] save failed — report kept in memory only');
  }

  env.onReportGenerated(report);

  if (ctx.siteId && !(report.siteId === ctx.siteId)) {
    report = { ...report, siteId: ctx.siteId };
  }

  if (ctx.siteId && report.id) {
    try {
      await env.updateSiteReportIds(ctx.siteId, report.id);
    } catch (e) {
      console.warn('[finalizeReportAfterPrecedenceResolution] reportIds sync failed:', (e as Error)?.message);
    }
  }

  if (env.applyReportToSed && ctx.siteId && sed.id) {
    try {
      await env.applyReportToSed(report, ctx.siteId, sed.id);
    } catch (e) {
      console.warn('[finalizeReportAfterPrecedenceResolution] applyReportToSed failed:', (e as Error)?.message);
    }
  }

  const { logAuditEvent } = await import('../telemetry/auditTelemetry');
  logAuditEvent({
    type: 'report-generated',
    siteId: ctx.siteId,
    reportId: report.id,
    reportType: report.reportType ?? ctx.reportTypeId,
    payload: {
      degradedMode: report.degradedMode ?? false,
      promptVersion: report.promptVersion,
      precedenceResolved: true,
    },
  });

  if (ctx.siteId) {
    const { enqueueCompositeRecompute } = await import('../portfolio/enqueueCompositeRecompute');
    enqueueCompositeRecompute(ctx.siteId);
  }

  return report;
}
